import { PersonMapping, PersonMappingSelectType, personMappingOptions } from '@bp/shared-planung-utils';
import { Select, SelectOptionType } from '@bp/ui-components';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { naturalStringSort } from '../../../utils/stringHelper';
import { XlsxFileImport, XlsxImportColumn } from '../types/XlsxImportTypes';
import { getPersonMappingLabel } from '../utils/XlsxTranslationUtils';
import styles from './_XlsxImportForms.module.scss';

type XlsxTeacherImportFormProps = {
  fileImport: XlsxFileImport;
  initialColumns: XlsxImportColumn<PersonMapping>[];
  onColumnsChanged: (columns: XlsxImportColumn<PersonMapping>[]) => void;
};

export const XlsxTeacherImportForm = ({ fileImport, initialColumns, onColumnsChanged }: XlsxTeacherImportFormProps) => {
  const { t } = useTranslation();

  const [columns, setColumns] = useState<XlsxImportColumn<PersonMapping>[]>(initialColumns);

  const columnOptions: PersonMappingSelectType[] = useMemo(
    () =>
      personMappingOptions
        .map((option) => ({
          label: getPersonMappingLabel(option),
          value: option,
        }))
        .sort((a, b) => naturalStringSort(a.label, b.label)),
    [],
  );

  const remainingColumnOptions: PersonMappingSelectType[] = useMemo(
    () => columnOptions.filter((option) => columns.some((c) => c.content === option.value && c.index === null)),
    [columnOptions, columns],
  );

  useEffect(() => {
    setColumns(initialColumns);
  }, [initialColumns]);

  return (
    <div className={styles['xlsx-import-form']}>
      <div className={styles.table}>
        <table>
          <thead>
            <tr>
              {fileImport.headers.map((h, index) => {
                return (
                  <th key={h}>
                    {
                      <Select
                        options={remainingColumnOptions.map((option) => ({
                          label: option.label,
                          value: option.value,
                          group:
                            option.value === 'firstName' || option.value === 'lastName'
                              ? t('import.mandatoryFields')
                              : t('import.optionalFields'),
                        }))}
                        hasGroups
                        value={columnOptions.find((option) => {
                          const mappingItem = columns.find((c) => c.index === index);
                          return mappingItem && option.value === mappingItem.content;
                        })}
                        onChange={async (e) => {
                          const selectedValue = e ? ((e as SelectOptionType).value as PersonMapping) : null;

                          // Clear any existing mapping for this column
                          const updatedValues = columns.map((item) =>
                            item.index === index ? { ...item, index: null } : item,
                          );

                          // If a value is selected, find the mapping item and set the column index
                          if (selectedValue) {
                            const mappingIndex = updatedValues.findIndex((item) => item.content === selectedValue);
                            if (mappingIndex !== -1) {
                              updatedValues[mappingIndex].index = index;
                            }
                          }

                          // Update the entire form values array
                          setColumns(updatedValues);
                          onColumnsChanged(updatedValues);
                        }}
                        name={`column-${index}`}
                        dense
                        isClearable
                        placeholder={t('import.ignoreColumn')}
                        menuPosition='fixed'
                      />
                    }
                  </th>
                );
              })}
            </tr>
            <tr>
              {fileImport.headers.map((h) => {
                return <th key={h}>{h}</th>;
              })}
            </tr>
          </thead>
          <tbody>
            {fileImport.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((entry, entryIndex) => {
                  return <td key={`${rowIndex}-${entryIndex}`}>{entry}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
